import { makeStyles } from 'tss-react/mui';

import { OPTION_ROW_HEIGHT_PX } from './RecipientOption.styles';

const FIELD_HEIGHT_PX = 48;
const CHIP_HEIGHT_PX = 20;
const CHIP_RADIUS_PX = 4;
const CHIP_ICON_SIZE_PX = 12;
/**
 * Transparent bleed on each side of the close glyph, taking its target
 * from the drawn 12px to 16px. It stays inside the 20px chip on every
 * edge, so the chip's own `overflow: hidden` does not clip it.
 */
const CHIP_DELETE_TARGET_BLEED_PX = 2;
const MAX_VISIBLE_OPTIONS = 5;

/**
 * Recipients field, its chips, and the suggestion dropdown, per the
 * Figma share-via-email modal.
 *
 * The focus ring is drawn as a doubled box-shadow — a white halo then
 * the brand tint — rather than an `outline`, so it hugs the field's 8px
 * radius instead of boxing it.
 */
const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
  },
  field: {
    '& .MuiOutlinedInput-root': {
      minHeight: FIELD_HEIGHT_PX,
      padding: `0 ${theme.new.spacing.small}`,
      gap: theme.new.spacing.xsmall,
      borderRadius: theme.new.shape.borderRadius.small,
      backgroundColor: theme.new.color.background.input,
      ...theme.typography.small_body_regular,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.new.color.line.border,
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.new.color.common.white}, 0 0 0 4px #c2dff9`,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1,
      borderColor: theme.new.color.line.border,
    },
  },
  // The theme styles `.MuiAutocomplete-paper` at six classes of
  // specificity, and the dropdown is drawn tighter than that: an 8px
  // radius over 12px, a full border over a hairline, one elevation step
  // lower. Outweighing it is the only way to land those values.
  paper: {
    '&&&&&&&': {
      borderRadius: theme.new.shape.borderRadius.small,
      border: `1px solid ${theme.new.color.line.border}`,
      boxShadow: theme.new.boxShadows.elevation1,
      // Rows run edge to edge, so the corners have to clip them.
      overflow: 'hidden',
    },
  },
  listbox: {
    '&&&': {
      // Rows carry their own padding and the first sits flush with the
      // paper's top edge, which MUI's list padding would push off.
      padding: 0,
      maxHeight: MAX_VISIBLE_OPTIONS * OPTION_ROW_HEIGHT_PX,
    },
  },
  // MUI draws the loading line at the paper's inherited body size, a
  // step larger than the rows it stands in for. Doubled to outweigh
  // MUI's own single-class rule, which keeps the secondary grey.
  loading: {
    '&&': {
      ...theme.typography.small_body_regular,
      color: theme.new.color.textSecondary,
    },
  },
  chip: {
    height: CHIP_HEIGHT_PX,
    borderRadius: CHIP_RADIUS_PX,
    backgroundColor: theme.new.color.grey[500],
    color: theme.new.color.common.white,
    '&:hover': {
      backgroundColor: theme.new.color.grey[600],
    },
    '& .MuiChip-label': {
      padding: `0 ${theme.new.spacing.xsmall}`,
      ...theme.typography.caption_medium,
    },
    '& .MuiChip-deleteIcon': {
      position: 'relative',
      width: CHIP_ICON_SIZE_PX,
      height: CHIP_ICON_SIZE_PX,
      padding: 0,
      margin: `0 ${theme.new.spacing.xsmall} 0 0`,
      color: theme.new.color.common.white,
      '&:hover': {
        color: theme.new.color.common.white,
        backgroundColor: 'transparent',
      },
      // Grows the pressable area without touching the box the glyph is
      // laid out in, so widening it cannot shift the label or the
      // chip's drawn spacing.
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: -CHIP_DELETE_TARGET_BLEED_PX,
      },
    },
  },
  // A malformed address differs from a sound one in hue alone: it
  // keeps the chip's size, radius, and white label, so the two read as
  // one component in two states rather than two components. Declared
  // after `chip` so it outweighs the grey at equal specificity.
  chipInvalid: {
    backgroundColor: theme.new.color.danger[600],
    // Restating the red under hover is what keeps it: `chip`'s hover
    // rule carries a pseudo-class this would otherwise lose to, and a
    // malformed chip turning grey under the pointer would drop the one
    // cue saying why Send refuses.
    '&:hover': {
      backgroundColor: theme.new.color.danger[600],
    },
  },
}));

export default useStyles;
